import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  ConfirmDialog,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { paymentsApi, type PaymentRequest } from "@/services/api";
import {
  Send,
  ArrowLeft,
  Loader2,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Users,
  Banknote,
  RefreshCw,
  Circle,
} from "lucide-react";
import { formatDate, cn } from "@/lib/utils";
import { toast } from "sonner";

const statusConfig: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "success" | "warning" | "destructive";
    description: string;
    icon: React.ReactNode;
  }
> = {
  DRAFT: {
    label: "Rascunho",
    variant: "secondary",
    description: "Pagamento em rascunho",
    icon: <FileText className="w-4 h-4" />,
  },
  PENDING_APPROVAL: {
    label: "Aguardando Aprovação",
    variant: "warning",
    description: "Seu pagamento está em análise pela equipe",
    icon: <Clock className="w-4 h-4" />,
  },
  APPROVED: {
    label: "Aprovado",
    variant: "success",
    description: "Pagamento aprovado, aguardando processamento",
    icon: <CheckCircle className="w-4 h-4" />,
  },
  PROCESSING: {
    label: "Processando",
    variant: "default",
    description: "Pagamento em processamento",
    icon: <Loader2 className="w-4 h-4 animate-spin" />,
  },
  AWAITING_DEPOSIT: {
    label: "Aguardando Depósito",
    variant: "warning",
    description: "Aguardando depósito USDC no provedor",
    icon: <Clock className="w-4 h-4" />,
  },
  DEPOSITED: {
    label: "Depositado",
    variant: "default",
    description: "USDC depositado, processando envio",
    icon: <Banknote className="w-4 h-4" />,
  },
  COMPLETED: {
    label: "Concluído",
    variant: "success",
    description: "Pagamento enviado com sucesso",
    icon: <CheckCircle className="w-4 h-4" />,
  },
  FAILED: {
    label: "Falha",
    variant: "destructive",
    description: "Ocorreu uma falha no processamento",
    icon: <AlertTriangle className="w-4 h-4" />,
  },
  CANCELLED: {
    label: "Cancelado",
    variant: "secondary",
    description: "Pagamento foi cancelado",
    icon: <XCircle className="w-4 h-4" />,
  },
  REJECTED: {
    label: "Rejeitado",
    variant: "destructive",
    description: "Pagamento rejeitado pela equipe",
    icon: <XCircle className="w-4 h-4" />,
  },
};

const railLabels: Record<string, string> = {
  wire: "Wire Transfer",
  sepa: "SEPA",
  spei: "SPEI",
};

function formatFiatAmount(value: string | number | undefined, currency: string): string {
  if (!value) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  const localeMap: Record<string, string> = { USD: "en-US", EUR: "de-DE", MXN: "es-MX" };
  return new Intl.NumberFormat(localeMap[currency] || "en-US", {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatUsdc(value: string | number | undefined): string {
  if (!value) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  return (
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(num) + " USDC"
  );
}

// Timeline steps
const timelineSteps = [
  { status: "PENDING_APPROVAL", label: "Solicitado" },
  { status: "APPROVED", label: "Aprovado" },
  { status: "PROCESSING", label: "Processando" },
  { status: "AWAITING_DEPOSIT", label: "Aguardando depósito" },
  { status: "DEPOSITED", label: "Depositado" },
  { status: "COMPLETED", label: "Concluído" },
];

const statusOrder: Record<string, number> = {
  DRAFT: 0,
  PENDING_APPROVAL: 1,
  APPROVED: 2,
  PROCESSING: 3,
  AWAITING_DEPOSIT: 4,
  DEPOSITED: 5,
  COMPLETED: 6,
};

export function DetalhePagamentoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [payment, setPayment] = useState<PaymentRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const loadPayment = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await paymentsApi.getPayment(id);
      setPayment(data);
    } catch (error) {
      console.error("Erro ao carregar pagamento:", error);
      toast.error("Erro ao carregar pagamento", {
        style: { background: "#495565", color: "#ffffff" },
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPayment();
  }, [loadPayment]);

  const handleCancel = async () => {
    if (!id) return;
    try {
      setCancelling(true);
      await paymentsApi.cancelPayment(id);
      toast.success("Pagamento cancelado", {
        description: "O saldo USDC foi desbloqueado.",
        style: { background: "#495565", color: "#ffffff" },
      });
      loadPayment();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Erro desconhecido";
      toast.error("Erro ao cancelar", {
        description: message,
        style: { background: "#495565", color: "#ffffff" },
      });
    } finally {
      setCancelling(false);
      setCancelDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Pagamento não encontrado</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/pagamentos")}>
          Voltar para lista
        </Button>
      </div>
    );
  }

  const status = statusConfig[payment.status] || {
    label: payment.status,
    variant: "secondary" as const,
    description: "",
    icon: <Circle className="w-4 h-4" />,
  };

  const canCancel = payment.status === "PENDING_APPROVAL";
  const isTerminal = ["COMPLETED", "CANCELLED", "REJECTED", "FAILED"].includes(payment.status);
  const currentStatusIndex = statusOrder[payment.status] ?? -1;

  const beneficiaryName =
    payment.beneficiary?.beneficiaryName ||
    payment.inlineBeneficiaryName ||
    "-";

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Send className="h-5 w-5 sm:h-6 sm:w-6" />}
        title={payment.paymentCode}
        description={status.description}
        actions={
          <div className="flex gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={loadPayment}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => navigate("/pagamentos")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>
        }
      />

      {/* Status badge */}
      <div className="flex items-center gap-3">
        <Badge variant={status.variant} className="text-sm px-3 py-1">
          {status.icon}
          <span className="ml-1">{status.label}</span>
        </Badge>
        {payment.rejectionReason && (
          <span className="text-sm text-red-600">
            Motivo: {payment.rejectionReason}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Card: Payment Data */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Dados do Pagamento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-sm text-muted-foreground">Valor</p>
                <p className="font-semibold text-lg">
                  {formatFiatAmount(payment.amount, payment.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Moeda / Rail</p>
                <p className="font-medium">
                  {payment.currency} - {railLabels[payment.paymentRail] || payment.paymentRail}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fornecedor</p>
                <p className="font-medium">{payment.supplierName || "-"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Invoice</p>
                <p className="font-medium">{payment.invoiceNumber || "-"}</p>
              </div>
              {payment.exchangeRate && (
                <div>
                  <p className="text-sm text-muted-foreground">Cotação</p>
                  <p className="font-medium">
                    1 USDC = {parseFloat(payment.exchangeRate).toFixed(4)} {payment.currency}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Data</p>
                <p className="font-medium">{formatDate(payment.createdAt)}</p>
              </div>
            </div>
            {payment.description && (
              <div>
                <p className="text-sm text-muted-foreground">Descrição</p>
                <p className="text-sm">{payment.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Card: Costs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Banknote className="w-4 h-4" />
              Custos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {payment.serviceFeeAmount && parseFloat(payment.serviceFeeAmount) > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Taxa de serviço</span>
                <span className="font-medium">
                  ${parseFloat(payment.serviceFeeAmount).toFixed(2)} USD
                  {payment.serviceFeeType && (
                    <span className="text-xs text-muted-foreground ml-1">
                      ({payment.serviceFeeType === "PERCENTAGE"
                        ? `${(parseFloat(payment.serviceFeeRate || "0") * 100).toFixed(2)}%`
                        : "fixo"})
                    </span>
                  )}
                </span>
              </div>
            )}
            {payment.providerFlatFeeUsd && parseFloat(payment.providerFlatFeeUsd) > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">
                  Taxa da rede ({railLabels[payment.paymentRail]})
                </span>
                <span className="font-medium">
                  ${parseFloat(payment.providerFlatFeeUsd).toFixed(2)} USD
                </span>
              </div>
            )}
            {payment.amountUsdc && (
              <div className="flex justify-between border-t pt-3">
                <span className="font-semibold">Total USDC bloqueado</span>
                <span className="font-bold text-blue-600">
                  {formatUsdc(payment.amountUsdc)}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Card: Beneficiary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Beneficiário
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-muted-foreground">Nome</p>
              <p className="font-medium">{beneficiaryName}</p>
            </div>
            {payment.beneficiary && (
              <div>
                <p className="text-sm text-muted-foreground">Apelido</p>
                <p className="font-medium">{payment.beneficiary.nickname}</p>
              </div>
            )}
            {/* Wire details */}
            {payment.paymentRail === "wire" && (
              <>
                {(payment.inlineRoutingNumber || payment.inlineBankAccountNumber) && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Routing Number</p>
                      <p className="font-medium">{payment.inlineRoutingNumber || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Conta</p>
                      <p className="font-medium">{payment.inlineBankAccountNumber || "-"}</p>
                    </div>
                  </div>
                )}
                {payment.inlineBankName && (
                  <div>
                    <p className="text-sm text-muted-foreground">Banco</p>
                    <p className="font-medium">{payment.inlineBankName}</p>
                  </div>
                )}
              </>
            )}
            {/* SEPA details */}
            {payment.paymentRail === "sepa" && (
              <>
                {payment.inlineIban && (
                  <div>
                    <p className="text-sm text-muted-foreground">IBAN</p>
                    <p className="font-medium font-mono">{payment.inlineIban}</p>
                  </div>
                )}
                {payment.inlineBic && (
                  <div>
                    <p className="text-sm text-muted-foreground">BIC / SWIFT</p>
                    <p className="font-medium">{payment.inlineBic}</p>
                  </div>
                )}
              </>
            )}
            {/* SPEI details */}
            {payment.paymentRail === "spei" && payment.inlineSpeiClabe && (
              <div>
                <p className="text-sm text-muted-foreground">CLABE</p>
                <p className="font-medium font-mono">{payment.inlineSpeiClabe}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Card: Result (when completed) */}
        {payment.status === "COMPLETED" &&
          (payment.finalFiatAmount || payment.initialCryptoAmount) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Resultado
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {payment.finalFiatAmount && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Valor recebido pelo beneficiário
                    </span>
                    <span className="font-semibold text-green-600">
                      {formatFiatAmount(payment.finalFiatAmount, payment.currency)}
                    </span>
                  </div>
                )}
                {payment.initialCryptoAmount && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">USDC consumido</span>
                    <span className="font-medium">
                      {formatUsdc(payment.initialCryptoAmount)}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
      </div>

      {/* Timeline */}
      {!isTerminal || payment.status === "COMPLETED" ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Status do Processamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {timelineSteps.map((step, index) => {
                const stepOrder = statusOrder[step.status] ?? index;
                const isCompleted = currentStatusIndex > stepOrder;
                const isCurrent = payment.status === step.status;

                return (
                  <div key={step.status} className="flex items-center gap-2">
                    <div className="flex flex-col items-center min-w-[80px]">
                      <div
                        className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center",
                          isCompleted
                            ? "bg-green-100 text-green-700"
                            : isCurrent
                            ? "bg-blue-100 text-blue-700"
                            : "bg-muted text-muted-foreground"
                        )}
                      >
                        {isCompleted ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : isCurrent ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Circle className="w-4 h-4" />
                        )}
                      </div>
                      <span
                        className={cn(
                          "text-[10px] text-center mt-1",
                          isCurrent ? "font-semibold" : "text-muted-foreground"
                        )}
                      >
                        {step.label}
                      </span>
                    </div>
                    {index < timelineSteps.length - 1 && (
                      <div
                        className={cn(
                          "h-px w-8 flex-shrink-0",
                          isCompleted ? "bg-green-400" : "bg-border"
                        )}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Notes */}
      {payment.companyNotes && (
        <Card>
          <CardHeader>
            <CardTitle>Suas Observações</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{payment.companyNotes}</p>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      {canCancel && (
        <div className="flex justify-end">
          <Button
            variant="destructive"
            onClick={() => setCancelDialogOpen(true)}
            disabled={cancelling}
          >
            {cancelling ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <XCircle className="w-4 h-4 mr-2" />
            )}
            Cancelar Pagamento
          </Button>
        </div>
      )}

      {/* Cancel dialog */}
      <ConfirmDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        title="Cancelar Pagamento"
        description={`Tem certeza que deseja cancelar o pagamento ${payment.paymentCode}? O saldo USDC será desbloqueado.`}
        confirmText="Confirmar Cancelamento"
        variant="danger"
        onConfirm={handleCancel}
        loading={cancelling}
      />
    </div>
  );
}
